import { ActionButtonWithLoading } from '@components/ActionButtonWithLoading/ActionButtonWithLoading';
import { TriangleExclamationMarkIcon } from '@components/Icons/TriangleExclamationMarkIcon';
import { MobileModal } from '@components/MobileModal/MobileModal';
import { UnfurlItem } from '@lib/external/unfurl';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import type { IngestEstimate } from '@lib/utils/ingestEstimate';
import { OwnerAffordability } from '@lib/utils/ownerAffordability';
import { Tooltip } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import {
  BaseModal,
  BaseModalRef,
} from '@pages/Search/components/MomentGrid/components/BaseModal/BaseModal';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { useModalRegistration } from '@stores/ModalStore';
import { trackingStore } from '@stores/Tracking';
import { useUploadURL } from '@stores/UploadURL/UploadURLStore';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import css from './ChooseMediaModal.module.scss';
import { MediaDataGrid, MediaDataGridRef } from './ChooseMediaModal/components/MediaDataGrid';
import { ChooseMediaModalFooter } from './ChooseMediaModalFooter';
import { MobileSelectableMediaList, SelectableMediaItem } from './MobileSelectableMediaList';
import { UploadDisabledState } from './uploadDisabledState';
import { URLValidationModal, URLValidationModalRef } from './URLValidationModal';

type ChooseMediaModalProps = {
  onClose?: () => void;
  onOpen?: () => void;
  // Data
  dataWithHashes: UnfurlItem[];
  existingUniqueIds: Record<string, string>;
  isGeneratingHashes: boolean;
  hashGenerationError: string | null;
  // Selection
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  nonDuplicateSelectedCount: number;
  // Billing (pre-computed)
  creditCost: number;
  creditsLeft: number | null;
  creditsAfterProcessing: number | null;
  storageLeft: number;
  storageAfterProcessing: number;
  ingestEstimate: IngestEstimate;
  /**
   * True when the target library is billed to another account, so the credit and storage figures
   * above are the uploader's rather than the payer's. Swaps the footer stats for the owner
   * notice; the top-up CTA and the balance gate are already suppressed upstream in
   * `useUrlUploadFlow`.
   */
  billedToAnotherAccount: boolean;
  /** The owner's display name, when the backend gave one. Only read while the flag above is set. */
  ownerName?: string;
  /**
   * Gated upstream already (it's baked into `isUploadDisabled`); passed on so the footer badge
   * can say *why* Add is dead, which is the only explanation a touch device gets.
   */
  ownerAffordability?: OwnerAffordability;

  // Counts
  nSelected: { total: number; filteredOut: number; alreadyInLibrary: number };
  onFilteredOutCountChange: (count: number) => void;
  onAlreadyInLibraryCountChange: (count: number) => void;
  // Upload state
  isUploadDisabled: UploadDisabledState;
  isUploading: boolean;
  // Warning
  hasCreditOrStorageWarning: boolean;
  warningActionLabel: string;
  warningActionTooltip: string;
  onWarningAction: () => void;
  isToppingUp: boolean;
  // Mobile
  mobileListItems: SelectableMediaItem[];
  onMobileToggle: (id: string) => void;
  onMobileSelectAll: () => void;
  onMobileDeselectAll: () => void;
  // Actions
  onUploadButtonClick: () => void;
};

export const ChooseMediaModal = observer(
  ({
    onClose,
    onOpen,
    dataWithHashes,
    existingUniqueIds,
    isGeneratingHashes,
    hashGenerationError,
    selectedIds,
    onSelectionChange,
    nonDuplicateSelectedCount,
    creditCost,
    creditsLeft,
    creditsAfterProcessing,
    storageLeft,
    storageAfterProcessing,
    ingestEstimate,
    billedToAnotherAccount,
    ownerName,
    ownerAffordability,
    nSelected,
    onFilteredOutCountChange,
    onAlreadyInLibraryCountChange,
    isUploadDisabled,
    isUploading,
    hasCreditOrStorageWarning,
    warningActionLabel,
    warningActionTooltip,
    onWarningAction,
    isToppingUp,
    mobileListItems,
    onMobileToggle,
    onMobileSelectAll,
    onMobileDeselectAll,
    onUploadButtonClick,
  }: ChooseMediaModalProps) => {
    const isMobile = useIsMobile();
    const baseModalRef = useRef<BaseModalRef>(null);
    const urlValidationModalRef = useRef<URLValidationModalRef>(null);
    const mediaGridRef = useRef<MediaDataGridRef>(null);
    const uploadURLStore = useUploadURL();
    const libraryFolder = useLibraryFolder();
    // One source of truth for both branches. Previously the mobile branch registered from this
    // mount effect while the desktop branch registered from BaseModal's `onOpen` with no matching
    // unregister — balanced only by accident, because the host mount-gates on
    // `flowState === 'selecting'`. It also meant `hasOpenModals` stayed true after the user closed
    // the modal, until the whole flow unmounted.
    const [open, setOpen] = useState(false);

    useModalRegistration('choose-media', open);

    // Open on mount: this component is only rendered when flowState === 'selecting'.
    useEffect(() => {
      setOpen(true);
      trackingStore.chooseMediaModalOpened({
        totalVideos: dataWithHashes.length,
        libraryId: libraryFolder.library?.libraryId ?? 'unknown',
      });
      onOpen?.();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The desktop shell owns its open state behind a ref, so push ours down to it. Keyed on
    // `isMobile` too, so flipping between branches while open re-opens the other shell rather than
    // leaving both closed.
    //
    // Only the OPEN direction is pushed, deliberately. `BaseModal.handleClose` calls `onClose?.()`
    // unconditionally — even when it is already closed — so an `else baseModalRef.current.close()`
    // here would fire `handleClose` on mount (this effect first runs before `setOpen(true)` has
    // been applied), reporting a spurious cancel and tearing the whole flow down. Nothing needs the
    // close direction anyway: `open` only ever goes false because BaseModal closed itself.
    useEffect(() => {
      if (isMobile) return;
      if (open) baseModalRef.current?.open();
    }, [isMobile, open]);

    const handleClose = () => {
      trackingStore.chooseMediaModalCancelled({
        libraryId: libraryFolder.library?.libraryId ?? 'unknown',
      });
      setOpen(false);
      onClose?.();
    };

    const nSelectedVideos = nonDuplicateSelectedCount;

    const footerContent = (
      <div className={css.footer}>
        <ChooseMediaModalFooter
          nSelected={nSelected}
          creditStats={{
            current: creditsLeft ?? 0,
            cost: creditCost,
            left: creditsAfterProcessing ?? -1,
          }}
          storageStats={{
            current: storageLeft,
            cost: ingestEstimate.durationSeconds,
            left: storageAfterProcessing,
          }}
          hasUnavailableInfo={ingestEstimate.hasUnknownDuration}
          billedToAnotherAccount={billedToAnotherAccount}
          ownerName={ownerName}
          ownerAffordability={ownerAffordability}
        />
        <span style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Tooltip
            title={
              uploadURLStore.errorEntries.length > 0
                ? 'View and fix errored URLs'
                : 'Add or edit URLs if needed'
            }
            placement="top"
            arrow
          >
            <span>
              <ActionButton
                variant="secondary"
                onClick={() => {
                  trackingStore.chooseMediaEditUrlsClicked({
                    errorCount: uploadURLStore.errorEntries.length,
                  });
                  urlValidationModalRef.current?.open();
                }}
                padding={32}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {uploadURLStore.errorEntries.length > 0 && (
                    <span className={css.warningIcon}>
                      <TriangleExclamationMarkIcon />
                    </span>
                  )}
                  Edit URLs
                </span>
              </ActionButton>
            </span>
          </Tooltip>
          {hasCreditOrStorageWarning && warningActionLabel && (
            <Tooltip title={warningActionTooltip} placement="top" arrow>
              <span>
                <ActionButton onClick={onWarningAction} disabled={isToppingUp} padding={32}>
                  {warningActionLabel}
                </ActionButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title={isUploadDisabled.reason} placement="top" arrow>
            <span>
              <ActionButtonWithLoading
                onClick={onUploadButtonClick}
                disabled={isUploadDisabled.value}
                variant={hasCreditOrStorageWarning ? 'secondary' : undefined}
                padding={32}
                isLoading={isUploading}
              >
                {`Add ${nSelectedVideos} media to Library`}
              </ActionButtonWithLoading>
            </span>
          </Tooltip>
        </span>
      </div>
    );

    const mobileFooter = (
      <div className={css.mobileFooter}>
        <div className={css.mobileFooterRow}>
          <ChooseMediaModalFooter
            mobile
            nSelected={{
              total: selectedIds.length,
              filteredOut: 0,
              alreadyInLibrary: 0,
            }}
            creditStats={{
              current: creditsLeft ?? 0,
              cost: creditCost,
              left: creditsAfterProcessing ?? -1,
            }}
            storageStats={{
              current: storageLeft,
              cost: ingestEstimate.durationSeconds,
              left: storageAfterProcessing,
            }}
            hasUnavailableInfo={ingestEstimate.hasUnknownDuration}
            billedToAnotherAccount={billedToAnotherAccount}
            ownerName={ownerName}
            ownerAffordability={ownerAffordability}
          />
          {hasCreditOrStorageWarning && (
            <div className={css.mobileFooterWarning}>{isUploadDisabled.reason}</div>
          )}
        </div>
        <div className={css.mobileFooterButtons}>
          {hasCreditOrStorageWarning && (
            <ActionButton
              variant="primary"
              onClick={onWarningAction}
              disabled={isToppingUp}
              fullWidth
            >
              {warningActionLabel}
            </ActionButton>
          )}
          <ActionButton onClick={onUploadButtonClick} disabled={isUploadDisabled.value} fullWidth>
            {`Add ${nSelectedVideos} media`}
          </ActionButton>
        </div>
      </div>
    );

    return (
      <>
        {isMobile ? (
          <MobileModal
            open={open}
            onClose={handleClose}
            title="Select media"
            footer={mobileFooter}
            rawContent
          >
            {isGeneratingHashes ? (
              <div className={css.loadingContainer}>
                <div>Processing media data...</div>
                {hashGenerationError && (
                  <div className={css.loadingError}>{hashGenerationError}</div>
                )}
              </div>
            ) : (
              <MobileSelectableMediaList
                items={mobileListItems}
                selectedIds={selectedIds}
                onToggle={onMobileToggle}
                onSelectAll={onMobileSelectAll}
                onDeselectAll={onMobileDeselectAll}
              />
            )}
          </MobileModal>
        ) : (
          <BaseModal
            ref={baseModalRef}
            title="Select media to add to your library"
            onClose={handleClose}
            footerContent={footerContent}
            className={css.baseModal}
          >
            <div className={css.contentContainer}>
              {isGeneratingHashes ? (
                <div className={css.loadingContainer}>
                  <div>Processing media data...</div>
                  {hashGenerationError && (
                    <div className={css.loadingError}>{hashGenerationError}</div>
                  )}
                </div>
              ) : (
                <MediaDataGrid
                  ref={mediaGridRef}
                  data={dataWithHashes}
                  onSelectionChange={onSelectionChange}
                  selectedIds={selectedIds}
                  existingUniqueIds={existingUniqueIds}
                  onFilteredOutCountChange={onFilteredOutCountChange}
                  onVisibleSelectedDuplicatesCountChange={onAlreadyInLibraryCountChange}
                />
              )}
            </div>
          </BaseModal>
        )}
        <URLValidationModal ref={urlValidationModalRef} />
      </>
    );
  },
);
